import React from 'react';
import classnames from 'classnames';
import without from 'lodash/without';
import includes from 'lodash/includes';
import {FormattedMessage} from 'react-intl';
import {func, object, instanceOf} from 'prop-types';
import {Message, Container, Menu} from 'semantic-ui-react';
import IndexedSubSectionWrapper from 'hoc/IndexedSubSectionWrapper';
import ResourceFormProxy from 'classes/ResourceFormProxy';
import {NumberNames, createResultText} from './utils';

export default class DecisionTree extends React.Component {
  static propTypes = {
    formProxy: instanceOf(ResourceFormProxy).isRequired,
    chosenResults: object.isRequired,
    onSelectResult: func.isRequired,
    // onSelectAll: func.isRequired,
    // onReset: func.isRequired,
    // step: object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      // initialize every question and answer accordion with a closed state.
      accordions: props.personalityQuiz.questions.reduce((accordions, question) => {
        accordions[this.getAccordionKey(question)] = false;

        for (const answer of question.answers) {
          accordions[this.getAccordionKey(question, answer)] = false;
        }

        return accordions;
      }, {})
    };
  }

  // returns a unique key for any question or answer accordion.
  getAccordionKey = (...args) => args.map(({id}) => id).join('.');

  // returns a function that toggles the state of any question or answer accordion.
  toggleAccordion = key => {
    const nextState = {...this.state};
    nextState.accordions[key] = !nextState.accordions[key];
    this.setState(nextState);
  };

  // returns question accordions with answer accordions nested in those, and result widgets nested in those.
  render() {
    return this.props.personalityQuiz.questions.map((question, questionIndex) => {
      const questionKey = this.getAccordionKey(question);
      const questionHeader = `Question ${question.displayOrder}: ${question.text}`;

      const questionProps = {
        animate: true,
        index: questionIndex,
        key: `question-${question.id}`,
        formProxy: this.props.formProxy,
        active: this.state.accordions[questionKey],
        toggleOpen: () => this.toggleAccordion(questionKey),
        header: questionHeader,
        headerKeyText: questionHeader
      };

      return (
        <IndexedSubSectionWrapper {...questionProps}>
          {question.answers.map((answer, answerIndex) => {
            const answerKey = this.getAccordionKey(question, answer);
            const answerHeader = `Answer ${answer.displayOrder}: ${answer.text || ''}`;
            const answerNumberName = NumberNames[answerIndex % NumberNames.length]; // for color classes.

            const answerProps = {
              className: `decision-tree-answer ${answerNumberName}`,
              animate: true,
              index: answerIndex,
              key: `answer-${answer.id}`,
              formProxy: this.props.formProxy,
              active: this.state.accordions[answerKey],
              toggleOpen: () => this.toggleAccordion(answerKey),
              header: answerHeader,
              headerKeyText: answerHeader
            };

            return (
              <IndexedSubSectionWrapper {...answerProps}>
                {this.props.personalityQuiz.results.map((result, resultIndex) => {
                  // results that have been chosen within this question.
                  const chosenResults = this.props.chosenResults[question.id][result.id];

                  // results chosen within this answer.
                  const chosenHere = includes(chosenResults, answer.id);

                  // results chosen in other answers.
                  const chosenElsewhere = without(chosenResults, answer.id).length > 0;

                  const resultClassName = classnames('decision-tree-result', {
                    // if a result is chosen both for this answer and another answer, this
                    // answer's color should take precedence over the "already chosen
                    // elsewhere" color (grey). so the order here does matter.
                    'chosen-elsewhere': chosenElsewhere,
                    'chosen-here': chosenHere
                  });

                  const handleClick = this.props[chosenHere ? 'clearResult' : 'selectResult'];

                  const resultProps = {
                    compact: true,
                    size: 'small',
                    className: resultClassName,
                    onClick: () => handleClick(result.id, answer.id, question.id),
                    children: createResultText(result),
                    key: resultIndex
                  };

                  return <Message {...resultProps} />;
                })}
                <Container textAlign='right'>
                  <Menu.Item onClick={() => this.props.selectAll(answer.id, question.id)}>
                    <FormattedMessage id='editbreak.steps.personality_quiz.result_type.decision_tree.select_all' />
                  </Menu.Item>
                  <Menu.Item onClick={() => this.props.clearAll(answer.id, question.id)}>
                    <FormattedMessage id='editbreak.steps.personality_quiz.result_type.decision_tree.reset' />
                  </Menu.Item>
                </Container>
              </IndexedSubSectionWrapper>
            );
          })}
        </IndexedSubSectionWrapper>
      );
    });
  }
};



// WEBPACK FOOTER //
// ./public/components/forms/CustomFields/DecisionTree/DecisionTree.jsx